import React from "react"
import {Chip, Progress, Spinner} from "@heroui/react"
import Badge from "@/components/general/ui/Badge"
import {
  IconTrendingUp,
  IconTrendingDown,
  IconClock,
  IconUsers,
  IconAlertCircle,
  IconFolder,
  IconArrowUp,
} from "@tabler/icons-react"
import {AlertsStats as AlertsStatsType} from "@/features/alerts/alertTypes"

interface AlertsStatsProps {
  loading?: boolean
  error?: string
  stats?: AlertsStatsType | null
}

export const AlertsStats: React.FC<AlertsStatsProps> = ({
  loading = false,
  error,
  stats,
}) => {
  // Show loading state
  if (loading) {
    return (
      <div className="grid grid-cols-4 justify-between gap-3">
        <div className="col-span-3 flex flex-col items-center justify-center rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
          <Spinner size="md" color="primary" />
          <p className="mt-2 text-sm text-neutral-600">Loading stats...</p>
        </div>
        <div className="col-span-1 flex flex-col items-center justify-center rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
          <Spinner size="sm" color="primary" />
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="grid grid-cols-4 justify-between gap-3">
        <div className="col-span-4 rounded-xl border border-red-200 bg-red-50 p-4 text-center">
          <IconAlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="text-sm text-red-800">Error loading stats: {error}</p>
        </div>
      </div>
    )
  }

  // Real data display
  if (stats) {
    const resolvedPercentage =
      stats.totalAlerts > 0
        ? Math.round((stats.resolvedAlerts / stats.totalAlerts) * 100)
        : 0

    return (
      <div className="grid grid-cols-4 justify-between gap-3">
        <div className="col-span-3 flex flex-col justify-between rounded-xl border border-neutral-200 bg-white p-4 shadow-sm transition-all hover:shadow-md">
          <div className="mb-1 flex items-center justify-between">
            <h4 className="text-sm font-medium text-neutral-600">
              Total Alerts
            </h4>
            <div className="flex items-center gap-1">
              <Badge variant="red">{stats.unresolvedAlerts} Active</Badge>
              <Badge variant="green">{stats.resolvedAlerts} Resolved</Badge>
            </div>
          </div>
          <div className="mb-2 text-2xl font-semibold text-neutral-800">
            {stats.totalAlerts}
          </div>
          <Progress
            color="success"
            value={resolvedPercentage}
            size="sm"
            className="h-1.5"
            showValueLabel={false}
          />
        </div>

        <div className="col-span-1 flex flex-col rounded-xl border border-neutral-200 bg-white p-4 shadow-sm transition-all hover:shadow-md">
          <div className="mb-1 flex items-center gap-2">
            <IconArrowUp className="h-4 w-4 text-red-500" />
            <h4 className="text-sm font-medium text-neutral-600">Escalated</h4>
          </div>
          <div className="mt-auto flex items-end justify-between">
            <div className="text-2xl font-semibold text-neutral-800">
              {stats.escalatedAlerts}
            </div>
            <Badge variant="red">Escalated</Badge>
          </div>
        </div>
      </div>
    )
  }

  // Empty stats for disabled state
  return (
    <div className="grid grid-cols-4 justify-between gap-3 opacity-60">
      <div className="col-span-3 flex flex-col rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
        <div className="mb-1 flex items-center gap-2">
          <IconAlertCircle className="h-4 w-4 text-neutral-400" />
          <h4 className="text-sm font-medium text-neutral-500">Total Alerts</h4>
        </div>
        <div className="text-2xl font-semibold text-neutral-300">--</div>
      </div>

      <div className="col-span-1 flex flex-col rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
        <div className="mb-1 flex items-center gap-2">
          <IconArrowUp className="h-4 w-4 text-neutral-400" />
          <h4 className="text-sm font-medium text-neutral-500">Escalated</h4>
        </div>
        <div className="text-2xl font-semibold text-neutral-300">--</div>
      </div>
    </div>
  )
}
